
import { useNavigate } from 'react-router-dom';

import { useSignal } from '@preact/signals-react';
import { Auth } from 'aws-amplify';
import { useRef, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { ErrorField } from '../../../types/ErrorField';
import { removeErrorField, getErrorMessage, getErrorMessageAsLabels } from '../../../utils/formErrorUtils';
import ErrorItemList, { ErrorItemType } from '../../atoms/SpanList';
import FileUpload from '../../molecules/FileUpload';
import PageForm from '../../templates/PageForm';
import appConfig from '../../../config/app-config';

type Form = {
    file: (File | null);
}

// Quando o ambiente é de produção, usamos uma URL fixa
const BASE_URL = appConfig.isProd
    ? 'https://ywtmfwiu2h.execute-api.us-east-1.amazonaws.com/v1'
    : appConfig.apis.blocklistApiUrl;
const BLOCK_LIST_PAGE = "/blocklist/0/40/createdAt/DESC";
const BLOCK_LIST_CHANGED = "/blocklist/changed";

const BlockListInputForm = () => {

    const navigate = useNavigate();
    const apiVersion = useRef('');

    const [isSubmitted, setIsSubmitted] = useState(false);

    const form = useSignal<Form>({
        file: null,
    });

    useEffect(() => {
        if (isSubmitted) {
            validateForm();
        }
    }, [form.value, isSubmitted]);

    const isLoadingForm = useSignal(false);
    const errorFields = useSignal<ErrorField[]>([]);

    const changeForm = (field: string, value: any) => {

        form.value = { ...form.value, [field]: value };

        removeErrorField(errorFields.value, field);

        if (field === 'file') {
            removeErrorField(errorFields.value, 'totalInvalids');
        }
    }

    const validateForm = () => {
        const newErrorFields: ErrorField[] = [];

        if (!form.value.file) {
            newErrorFields.push({ fieldName: 'file', message: 'Arquivo é obrigatório' });
        }

        errorFields.value = newErrorFields;
        return newErrorFields.length === 0;
    }

    //TODO remover essa validação no cliente (mover para o backend)
    const submitForm = async (action: string) => {
        setIsSubmitted(true);
        if (!validateForm()) {
            toast.error("Por favor, corrija os erros no formulário.");
            return;
        }

        isLoadingForm.value = true;
        const formData = new FormData();

        if (form.value.file !== null)
            formData.append('file', form.value.file);

        const token = await Auth.currentSession()
            .then((user) => user.getIdToken()
                .getJwtToken());

        await fetch(BASE_URL + "/blocklist/" + action, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                ...(form.value.file && { 'X-File-Name': form.value.file.name })
            },
            body: formData
        }).then(async (response) => {
            if (response.ok) {
                toast.info("CPFs em processamento...");
                try {
                    const responseData = await response.json();
                    // Navigate to the BlockListChanged page with the response data
                    isLoadingForm.value = false;
                    navigate(`${BLOCK_LIST_CHANGED}`, {
                        state: { responseData }
                    });
                } catch (err) {
                    console.error("Error parsing response:", err);
                    // If there's an error parsing the response, navigate with a default response
                    const defaultResponse = {
                        message: "Processamento iniciado.",
                        nameFile: form.value.file?.name || "Arquivo desconhecido",
                        dtStartProcess: new Date().toISOString(),
                        // TODO: get the user name from the session
                        nameUser: (await Auth.currentUserInfo())?.attributes?.name || "Usuário desconhecido",
                        status: "PROCESSING",
                        processDetails: {
                            success: [],
                            erros: [
                                // Empty array of error objects with registerNumber and reason
                            ],
                            invalidos: [
                                // Empty array of invalid objects with registerNumber and reason
                            ]
                        },
                        apiVersion: "0.0.1",
                        apiName: "Blocklist"
                    };
                    isLoadingForm.value = false;
                    navigate(`${BLOCK_LIST_CHANGED}`, {
                        state: { responseData: defaultResponse }
                    });
                }
            }
        }).catch((error) => {
            console.error("Error: ", error);
            toast.error("Erro ao enviar arquivo.");
            isLoadingForm.value = false;
            navigate(`${BLOCK_LIST_PAGE}`);
        });
    }

    const cancelForm = () => {
        navigate(BLOCK_LIST_PAGE);
    }


    return (

        <PageForm
            pageTitle='Blocklist - Edição'
            apiVersion={apiVersion.current}
            isLoading={isLoadingForm.value}
            breadcrumb={[{ label: 'Blocklist', url: BLOCK_LIST_PAGE }, { label: 'Blocklist - edição', url: '#' }]}
            controlSlot={(
                <div className='pt-4 text-right' >
                    <button type='button'
                        className='text-black bg-gradient-to-r from-slate-200 via-slate-300 to-slate-400 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded text-sm px-5 py-2.5 text-center mr-2 mb-2 disabled:opacity-50 disabled:cursor-not-allowed'
                        onClick={cancelForm}
                        disabled={isLoadingForm.value}
                    >
                        <div className="flex items-center">
                            Cancelar
                        </div>
                    </button>

                    <button type='button'
                        disabled={isLoadingForm.value}
                        className='text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded text-sm px-5 py-2.5 text-center mr-2 mb-2 disabled:opacity-50 disabled:cursor-not-allowed'
                        onClick={() => submitForm("add")}>
                        <div className="flex items-center">
                            Incluir Lista
                        </div>
                    </button>

                    <button type='button'
                        disabled={isLoadingForm.value}
                        className='text-white bg-gradient-to-r from-red-500 via-red-600 to-red-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded text-sm px-5 py-2.5 text-center mr-2 mb-2 disabled:opacity-50 disabled:cursor-not-allowed'
                        onClick={() => submitForm("del")}>
                        <div className="flex items-center">
                            Remover Lista
                        </div>
                    </button>
                </div>
            )}
        >
            {/* TODO: colocar o form em um componente a parte e substitui-lo de acordo com o response do input */}
            {(<div className="container mx-auto px-4 py-8">

                <div className="mt-10">
                    <FileUpload
                        id="file"
                        name="file"
                        label="Arquivo enviado:"
                        errorMessage={getErrorMessage(errorFields.value, 'file')}
                        onUploadFile={(file) => changeForm('file', file)}
                        onRemoveFile={() => changeForm('file', null)}
                        accept=".xls, .xlsx"
                        info="XLS ou XLSX (MAX. 2MB)"
                        disabled={isLoadingForm.value}
                    />


                    {getErrorMessage(errorFields.value, "totalInvalids") ?

                        <ErrorItemList
                            itemType={ErrorItemType.ERROR}
                            label="CPFs inválidos"
                            items={getErrorMessageAsLabels(errorFields.value, "totalInvalids")}
                        />

                        : null}
                </div>
            </div>)}
        </PageForm>
    );
};

export default BlockListInputForm;
